(ns app.domain.committee.model
  (:require [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.template.model :as template-model]
            [app.domain.util :as du]
            [app.i18n.model :as i18n-model]
            [core.db.child-entity :as child]
            [schema.core :as s]))

(def schedule-periods
  [:week :month])

(def committee-states
  [:committee-meeting.state/active
   :committee-meeting.state/passed
   :committee-meeting.state/cancelled])

(defmethod du/is-active? :entity.type/committee
  [{state :domain/state}]
  (= state :committee.state/active))

(def ^{:arglists '([group])} group-committees
  (child/child-entities-of-type-fn :entity.type/committee))

(def ^{:arglists '([meeting])} meeting-items
  (child/child-entities-of-type-fn :entity.type/committee-item))

(defn active-group-committees
  [group]
  (->> group
       group-committees
       (filter du/is-active?)))

(defn get-workspace-applicable-committees [{:keys [:domain/authority-group :workspace/approval-authority]}]
  (->> [authority-group approval-authority (:entity/parent authority-group) (:entity/parent approval-authority)]
       (remove nil?)
       (mapcat active-group-committees)))

(def committee-meetings
  (child/child-entities-of-type-fn :entity.type/committee-meeting))

(defn get-future-meetings [committee]
  (->> committee
       committee-meetings
       (filter (du/in-state-fn :committee-meeting.state/active))))

(def get-workspace-applicable-meetings
  (comp (partial mapcat get-future-meetings)
        get-workspace-applicable-committees))

(def agenda-template-variables
  #{"committee-name" "date" "items" "meeting-description" "group-timezone"
    "meeting-weekday" "meeting-month" "meeting-day" "meeting-year" "meeting-time"
    "meeting-type" "header-contact-info"})

(def agenda-item-template-variables
  #{"permit-number" "item-description" "workspace-address" "order-number"
    "roll-number" "applicants" "property-owners" "deficiencies"
    "open-deficiencies" "resolved-deficiencies"
    "submittal-date" "case-created-date" "violation-created-date"
    "last-inspection-date" "additional-properties" "parcel-ids" "addresses"
    "street-name" "address-number" "city"
    "unit-type" "unit-number" "unit-type-and-number"})

(defn agenda-item-picker-variables
  "Hide unit-related placeholders when `:feature-flag.id/property-db-units` is off."
  [feature-flags]
  (template-model/filter-unit-placeholders agenda-item-template-variables feature-flags))

(def agenda-deficiency-template-variables
  #{"reference" "description"})

(defn meeting-group [committee-meeting]
  (-> committee-meeting
      :entity/parent
      :entity/parent))

(defn meeting-item-group [meeting-item]
  (-> meeting-item
      :entity/parent
      meeting-group))

(defn committee-domain-group [committee]
  (let [group (:entity/parent committee)]
    (if (group-model/domain? group)
      group
      (:entity/parent group))))

(defn meeting-authority-groups [meeting]
  (-> meeting :entity/parent :committee/authority-groups set))

(def committee-name (i18n-model/translation-fallback-fn :committee/names :committee/name))
(def committee-description (i18n-model/translation-fallback-fn :committee/descriptions :committee/description))

(defmethod i18n-model/get-localized-string-attrs :entity.type/committee [_]
  [:committee/names :committee/descriptions])

(defmethod group-model/get-authority-group :entity.type/committee [committee]
  (let [parent (:entity/parent committee)]
    ;; When committee is under a domain group, return nil (no single authority group)
    ;; When committee is under an authority group, return the parent
    (when (group-model/application-or-upper-tier-authority? parent)
      parent)))

(defmethod group-model/get-authority-group :entity.type/committee-item [committee-item]
  (-> committee-item :committee-item/item group-model/get-authority-group))

(defn enabled-for?
  "Return `true`, if the committees and meetings feature is enabled for the permit type"
  [permit-type]
  (-> ptc-model/permit-types-with-committees
      permit-type
      boolean))

(defn sort-items
  [items]
  (sort-by (juxt :ordered/order :db/id) items))

(def end-meeting-actions
  "Possible actions for items in meeting when closing the meeting.
  `:signal` is always executed on the application phase."
  {:committee-item.selected-action/set-refused            {:signal    [:process.signal/refuse-application {}]
                                                           :postpone? false}
   :committee-item.selected-action/defer-only             {:signal    [:process.signal/defer-application {}]
                                                           :postpone? false}
   :committee-item.selected-action/defer-and-move-to-next {:signal    [:process.signal/defer-application {}]
                                                           :postpone? true}
   :committee-item.selected-action/approve                {:signal    [:process.signal/approve {}]
                                                           :postpone? false}
   :committee-item.selected-action/approve-in-principle   {:signal    [:process.signal/approve-in-principle {}]
                                                           :postpone? false}
   :committee-item.selected-action/move-to-next           {:postpone? true}
   :committee-item.selected-action/passed                 {:postpone? false
                                                           :tr-kw     :committee/meeting-item-handled}})

(s/defschema ItemSelectedAction
  (apply s/enum (keys end-meeting-actions)))

(def default-action-kw
  :committee-item.selected-action/passed)
